import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";

interface WinCelebrationProps {
  solution: string;
  onComplete: () => void;
}

export function WinCelebration({ solution, onComplete }: WinCelebrationProps) {
  const [showLetters, setShowLetters] = useState(false);
  const [showTapHint, setShowTapHint] = useState(false);
  
  useEffect(() => {
    // Start letter animation after a brief delay
    const showTimer = setTimeout(() => setShowLetters(true), 100);
    
    // Show tap hint after animations complete
    const tapHintTimer = setTimeout(() => setShowTapHint(true), 2000);
    
    return () => {
      clearTimeout(showTimer);
      clearTimeout(tapHintTimer);
    };
  }, []);

  const letters = solution.toUpperCase().split("");

  const handleDismiss = () => {
    onComplete();
  };

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm cursor-pointer"
      onClick={handleDismiss}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleDismiss();
        }
      }}
      aria-label="Tap to continue"
      data-debug-ignore-a11y
    >
      {/* Confetti particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 50 }).map((_, i) => (
          <div
            key={i}
            className="absolute w-3 h-3 rounded-full animate-confetti"
            style={{
              left: `${Math.random() * 100}%`,
              top: `-10%`,
              backgroundColor: [
                'hsl(var(--tile-correct))',
                'hsl(var(--tile-present))',
                'hsl(var(--primary))',
                '#FFD700',
                '#FF69B4',
              ][i % 5],
              animationDelay: `${Math.random() * 2}s`,
              animationDuration: `${2 + Math.random() * 2}s`,
              animationIterationCount: 'infinite',
            }}
          />
        ))}
      </div>
      
      {/* Victory text */}
      <div className="text-center space-y-8 pointer-events-none">
        <h2 
          className={cn(
            "text-4xl md:text-5xl font-bold text-primary transition-all duration-500",
            showLetters ? "opacity-100 scale-100" : "opacity-0 scale-50"
          )}
        >
          🎉 You Won! 🎉
        </h2>
        
        {/* Dancing letters */}
        <div className="flex justify-center gap-2">
          {letters.map((letter, index) => (
            <div
              key={index}
              className={cn(
                "w-14 h-14 md:w-16 md:h-16 flex items-center justify-center",
                "bg-tile-correct text-tile-correct-foreground",
                "text-2xl md:text-3xl font-bold rounded-lg",
                "transition-all duration-300",
                showLetters ? "opacity-100" : "opacity-0 translate-y-10"
              )}
              style={{
                transitionDelay: `${index * 100}ms`,
                animation: showLetters ? `dance 0.5s ease-in-out ${index * 0.1 + 0.5}s infinite alternate` : 'none',
              }}
            >
              {letter}
            </div>
          ))}
        </div>
        
        <p 
          className={cn(
            "text-muted-foreground text-lg transition-all duration-500 delay-700",
            showLetters ? "opacity-100" : "opacity-0"
          )}
        >
          Amazing job!
        </p>

        {/* Tap to continue hint */}
        <p 
          className={cn(
            "text-muted-foreground text-sm transition-all duration-500",
            showTapHint ? "opacity-100" : "opacity-0"
          )}
        >
          Tap anywhere to continue
        </p>
      </div>
    </div>
  );
}